import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { viewAgentMarginDetails, updateAgentMarginDetails } from "../../Redux/Actions/agentMarginDetailsActions";
import MainLayout from "../../Layout/mainLayout";
import AgentMarginDetailsPopup from "./agentMarginDetailsPopup";
import toast from "react-hot-toast";
import "./viewAgentMarginDetails.css";

const ViewAgentMarginDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [editPopup, setEditPopup] = useState(false);

  const fetchDetails = async () => {
    try {
      setPageLoading(true);
      const res = await dispatch(viewAgentMarginDetails(id));
      if (res?.statusCode === 200) {
        setData(res.data);
      }
    } catch {
      toast.error("Failed to load details");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id, dispatch]);

  const handleSave = async (values) => {
    const res = await dispatch(updateAgentMarginDetails(id, values));
    if (res?.statusCode === 200) {
      setEditPopup(false);
      fetchDetails();
    }
  };

  if (pageLoading) {
    return (
      <MainLayout>
        <div className="vamd-loader-wrap">
          <div className="vamd-loader" />
          <p className="vamd-loader-text">Loading details…</p>
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <div className="vamd-empty-wrap">
          <div className="vamd-empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <p className="vamd-empty-text">No Data Found.</p>
          <Link to="/agent_margin_details" className="vamd-back-link">← Back to List</Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="vamd-wrapper">

        <div className="vamd-header">
          <div className="vamd-header-left">
            <Link to="/agent_margin_details" className="vamd-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </Link>
            <div className="vamd-header-divider" />
            <div>
              <div className="vamd-breadcrumb">Agent Margin Details / List / View</div>
              <h1 className="vamd-title">{data?.role || "—"}</h1>
            </div>
          </div>
          <button className="vamd-edit-btn" onClick={() => setEditPopup(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit
          </button>
        </div>

        <div className="vamd-hero-card">
          <div className="vamd-hero-left">
            <div className="vamd-hero-avatar">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div>
              <span className="vamd-hero-label">Role</span>
              <span className="vamd-hero-name">{data?.role || "—"}</span>
            </div>
          </div>
          <div className="vamd-hero-meta">
            <div className="vamd-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {data?.created_at ? new Date(data.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
            </div>
          </div>
        </div>

        <div className="vamd-grid-layout">
          <div className="vamd-col">
            <div className="vamd-card">
              <div className="vamd-card-header">
                <span className="vamd-card-badge">01</span>
                <h2 className="vamd-card-title">Margin Information</h2>
              </div>
              <div className="vamd-info-grid">
                <div className="vamd-info-item">
                  <span className="vamd-info-label">Role</span>
                  <span className="vamd-info-value">{data?.role || "No Data"}</span>
                </div>
                <div className="vamd-info-item">
                  <span className="vamd-info-label">Lead From</span>
                  <span className="vamd-info-value">
                    {data?.lead_from
                      ? data.lead_from.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                      : "No Data"}
                  </span>
                </div>
                <div className="vamd-info-item">
                  <span className="vamd-info-label">Created At</span>
                  <span className="vamd-info-value">
                    {data?.created_at ? new Date(data.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "No Data"}
                  </span>
                </div>
                <div className="vamd-info-item vamd-info-item--full">
                  <span className="vamd-info-label">Note</span>
                  <span className="vamd-info-value">{data?.note || "No Data"}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="vamd-col">
            <div className="vamd-card">
              <div className="vamd-card-header">
                <span className="vamd-card-badge">02</span>
                <h2 className="vamd-card-title">Margin Details</h2>
              </div>
              <div className="vamd-margin-grid">
                <div className="vamd-margin-card vamd-margin-card--agent">
                  <div className="vamd-margin-card__label">Agent Margin</div>
                  <div className="vamd-margin-card__value">{data?.agent_margin ?? "—"}%</div>
                </div>
                <div className="vamd-margin-card vamd-margin-card--admin">
                  <div className="vamd-margin-card__label">Admin Margin</div>
                  <div className="vamd-margin-card__value">{data?.admin_margin ?? "—"}%</div>
                </div>
                <div className="vamd-margin-card vamd-margin-card--other">
                  <div className="vamd-margin-card__label">Other Margin</div>
                  <div className="vamd-margin-card__value">{data?.other_margin ?? "—"}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <AgentMarginDetailsPopup
        visible={editPopup}
        onClose={() => setEditPopup(false)}
        existingData={data}
        onSave={handleSave}
      />
    </MainLayout>
  );
};

export default ViewAgentMarginDetails;